import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Eye, Search } from "lucide-react";

const fmtDate = (s) => (s || "").slice(0, 16).replace("T", " ");

export const QueueTargetPreview = () => {
  const [searchParams] = useSearchParams();
  const [catalog, setCatalog] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [manualId, setManualId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/reader/jobs-catalog?page_size=300").then(({ data }) => {
      setCatalog(data.items || []);
      const pj = searchParams.get("preview_job");
      if (pj) {
        const found = (data.items || []).find((i) => i.job_id === pj);
        if (found) { setSelected(found); runPreview(pj); }
        else { setManualId(pj); runPreview(pj); }
      }
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((i) =>
      (i.filename || "").toLowerCase().includes(q) ||
      i.job_id.toLowerCase().includes(q) ||
      (i.pdf_sha256 || "").toLowerCase().startsWith(q));
  }, [catalog, query]);

  const runPreview = async (jid) => {
    const id = jid || selected?.job_id || manualId.trim();
    if (!id) { toast.error("Seleziona un job dalla coda o inserisci un job_id"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.get(`/reader/expert-cases/queue-target-preview?job_id=${id}`);
      setResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const gap = result?.target_profile?.warnings?.find((w) => w.code === "PACKAGE_PAGE_SEQUENCE_GAP");
  const prof = result?.target_profile;

  return (
    <div className="border-2 border-border bg-white p-3 text-xs space-y-3" data-testid="queue-preview-section">
      <div className="font-bold flex items-center gap-2"><Eye size={14} />
        Anteprima riferimenti da job in coda (READ-ONLY — anche AWAITING_READER_START, mai avvia il lettore, ZERO OpenAI)</div>

      <div className="flex flex-wrap items-start gap-2">
        <div className="relative">
          <div className="flex items-center gap-1 border border-border px-2 h-8 w-96 bg-white">
            <Search size={12} className="text-muted-foreground" />
            <input className="flex-1 outline-none text-xs" data-testid="jobs-catalog-search"
              placeholder="Cerca job per nome file, job_id o SHA…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)} />
          </div>
          {open && (
            <div className="absolute z-20 mt-0.5 w-[520px] max-h-64 overflow-auto border border-border bg-white shadow-lg" data-testid="jobs-catalog-dropdown">
              {filtered.map((i) => (
                <button key={i.job_id} data-testid={`jobs-catalog-option-${i.job_id}`}
                  className={`w-full text-left px-2 py-1.5 border-b border-border/50 hover:bg-muted ${!i.eligible_for_reference_preview ? "opacity-50" : ""}`}
                  onClick={() => { setSelected(i); setOpen(false); setQuery(""); }}>
                  <span className="font-bold">{i.filename}</span>
                  {" · "}<span className="text-[10px]">{i.status}</span>
                  {" · "}{fmtDate(i.created_at)}
                  {" · "}<span className="font-mono">{i.sha_abbrev}…</span>
                  {!i.eligible_for_reference_preview && <span className="text-red-700"> · {i.ineligibility_reason}</span>}
                </button>
              ))}
              {filtered.length === 0 && <div className="px-2 py-2 text-muted-foreground">Nessun job corrispondente</div>}
            </div>
          )}
        </div>
        <Button size="sm" onClick={() => runPreview()} disabled={loading} data-testid="queue-preview-run">
          {loading ? "Analisi deterministica…" : "Anteprima riferimenti (read-only)"}</Button>
      </div>

      {selected && (
        <div className="border border-border bg-muted/40 px-2 py-1.5" data-testid="jobs-catalog-selected">
          Job selezionato: <b>{selected.filename}</b> · {selected.status} · {fmtDate(selected.created_at)} ·
          sha <span className="font-mono">{selected.sha_abbrev}…</span> ·
          job_id <span className="font-mono" data-testid="selected-job-id">{selected.job_id}</span>
          {!selected.eligible_for_reference_preview && (
            <span className="text-red-700 font-bold"> — NON eleggibile: {selected.ineligibility_reason}</span>)}
        </div>
      )}

      <div className="flex items-center gap-2 text-muted-foreground">
        <span>Fallback tecnico (job_id manuale, rivalidato dal backend):</span>
        <Input placeholder="job_id" className="h-7 w-64 text-xs" value={manualId}
          onChange={(e) => setManualId(e.target.value)} data-testid="queue-preview-manual-jobid" />
      </div>

      {result && prof && (
        <div className="space-y-3" data-testid="queue-preview-result">
          {gap && (
            <div className="border-2 border-red-400 bg-red-50 p-2 text-red-900 font-bold flex items-start gap-2" data-testid="missing-page-warning">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                PACKAGE_PAGE_SEQUENCE_GAP — {gap.message}
                <div className="font-normal mt-1">Pagine attese: {gap.expected_pages} · ricevute: {gap.received_pages} · mancanti: [{gap.missing_pages.join(", ")}]. {prof.unknown_pages_note}.</div>
              </div>
            </div>
          )}

          <div className="border border-border p-2 space-y-1" data-testid="target-profile">
            <div className="font-bold">Profilo target deterministico ({prof.profile_builder_version}) — costruito PRIMA della consultazione GOLD, ZERO OpenAI</div>
            <div>Job: <b>{result.job.filename}</b> · stato <b data-testid="job-status-in-preview">{result.job.status}</b> (invariato) · sha <span className="font-mono">{(result.job.pdf_sha256 || "").slice(0, 12)}…</span></div>
            <div>Tipologia candidata: <b data-testid="doc-type-candidate">{prof.document_type_candidate.value || "UNKNOWN"}</b> {prof.document_type_candidate.evidence && <span className="text-muted-foreground">({prof.document_type_candidate.evidence})</span>}</div>
            <div>Assieme: <b>{prof.assembly_code.value || "UNKNOWN"}</b> · edizione/revisione: <b>{prof.revision.value ?? "UNKNOWN"}</b> {prof.revision.evidence && <span className="text-muted-foreground">({prof.revision.evidence})</span>}</div>
            <div>Materiale: <b>{prof.material.value || "UNKNOWN"}</b> (pag. {prof.material.pages.join(",") || "—"}) · Finitura: <b>{prof.finish.value || "UNKNOWN"}</b></div>
            <div>Cartigli: {prof.sheet_declarations.map((s) => `${s.sheet}/${s.of}`).join(" · ")}</div>
            <div>Ruoli pagina: {prof.page_roles.map((p) => `pag.${p.page}: ${p.roles.join("+")}`).join(" · ")}</div>
            {prof.bom.items?.length > 0 && (
              <table className="w-full mt-1 border border-border" data-testid="bom-table">
                <thead><tr className="bg-muted text-left">
                  {["Codice distinta", "Spessore", "Materiale (raw)"].map((h) => <th key={h} className="px-2 py-0.5">{h}</th>)}
                </tr></thead>
                <tbody>{prof.bom.items.map((b) => (
                  <tr key={b.code} className="border-t border-border/50">
                    <td className="px-2 py-0.5 font-mono">{b.code}</td>
                    <td className="px-2 py-0.5">{b.thickness_mm != null ? `${b.thickness_mm} mm` : "UNKNOWN"}</td>
                    <td className="px-2 py-0.5">{b.material_raw || "UNKNOWN"}</td>
                  </tr>))}
                </tbody>
              </table>
            )}
          </div>

          <div className="border border-border p-2 space-y-2" data-testid="selector-results">
            <div className="font-bold" data-testid="threshold-info">
              Riferimenti selezionati ({result.selected_references.length}/{result.max_references} max) — scoring {result.scoring_version} · soglia minima {result.threshold} · REFERENCE_AUGMENTATION={result.reference_augmentation}</div>
            {result.selected_references.length === 0 && (
              <div className="text-muted-foreground" data-testid="no-refs-selected">NESSUN riferimento sopra soglia: nessuna selezione forzata.</div>)}
            {result.selected_references.map((r) => (
              <div key={r.case_id} className="border border-emerald-300 bg-emerald-50/50 p-2" data-testid={`ref-rank-${r.rank}`}>
                <div className="font-bold">#{r.rank} — {r.case_id} · punteggio {r.score} (soglia {result.threshold})</div>
                <div className="text-muted-foreground">{r.title}</div>
                <table className="w-full mt-1">
                  <tbody>{r.breakdown.map((b) => (
                    <tr key={b.criterion} className="border-t border-border/40">
                      <td className="py-0.5 pr-2 font-mono text-[10px]">{b.criterion}</td>
                      <td className={`py-0.5 pr-2 font-bold ${b.points > 0 ? "text-emerald-800" : b.points < 0 ? "text-red-700" : "text-muted-foreground"}`}>{b.points > 0 ? `+${b.points}` : b.points}</td>
                      <td className="py-0.5 text-muted-foreground">{b.motivation}</td>
                    </tr>))}
                  </tbody>
                </table>
                {r.contradictions.length > 0 && (
                  <div className="text-red-700 mt-1">Contraddizioni: {r.contradictions.map((c) => `${c.criterion}: target ${c.target} vs caso ${c.case}`).join(" · ")}</div>)}
              </div>
            ))}
            {result.excluded.length > 0 && (
              <div className="text-red-800" data-testid="excluded-refs">
                Esclusi: {result.excluded.map((x) => `${x.case_id} — ${x.reason}`).join(" · ")}</div>)}
            <div className="text-muted-foreground">{result.note}</div>
          </div>
        </div>
      )}
    </div>
  );
};
